import { useEffect, useState } from "react";
import type { FaqEntry, Image, PricingConfig } from "@photography-booking/shared";
import { Nav } from "./components/Nav.js";
import { Hero } from "./sections/Hero.js";
import { Gallery } from "./sections/Gallery.js";
import { Investment } from "./sections/Investment.js";
import { About } from "./sections/About.js";
import { Faq } from "./sections/Faq.js";
import { Booking } from "./sections/Booking.js";
import { Footer } from "./sections/Footer.js";
import { DEFAULT_FAQS, DEFAULT_PRICING } from "./content/site-content.js";
import { fetchFaqs, fetchGallery, fetchHero, fetchPricing } from "./api/client.js";
import { useSiteTracker } from "./lib/use-site-tracker.js";

/** Live, owner-managed site content loaded from the public API routes. */
interface SiteData {
  gallery: readonly Image[];
  pricing: PricingConfig;
  faqs: readonly FaqEntry[];
  heroRenditionKey: string | null;
  heroPhotoCredit: string | null;
}

/**
 * Loads the public site content (gallery, pricing, FAQ, hero) on mount.
 *
 * Each fetch tolerates failure and resolves to a safe fallback, so the initial
 * render uses the documented defaults/placeholders and is replaced with live
 * data once the requests settle. The page always renders.
 */
function useSiteData(): SiteData {
  const [data, setData] = useState<SiteData>({
    gallery: [],
    pricing: DEFAULT_PRICING,
    faqs: DEFAULT_FAQS,
    heroRenditionKey: null,
    heroPhotoCredit: null,
  });

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      fetchGallery(),
      fetchPricing(),
      fetchFaqs(),
      fetchHero(),
    ]).then(([gallery, pricing, faqs, hero]) => {
      if (!cancelled) {
        setData({
          gallery,
          pricing,
          faqs,
          heroRenditionKey: hero.renditionKey,
          heroPhotoCredit: hero.photoCredit,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}

/**
 * Public single-page app shell with a dark theme (Req 1.1–1.5, 5.1–5.3).
 *
 * Sections are composed top to bottom. Owner-managed content (hero image,
 * gallery, pricing, FAQ) is fetched from the public API on mount and passed
 * into the relevant sections; until then the documented defaults render.
 *
 * {@link useSiteTracker} attaches the in-house engagement measurement (page
 * view, section views on hash navigation, flush on page hide). It never throws
 * and never affects what renders (Req 2.1, 2.2, 2.9, 14.5).
 */
export function App(): JSX.Element {
  const { gallery, pricing, faqs, heroRenditionKey, heroPhotoCredit } =
    useSiteData();

  useSiteTracker();

  return (
    <div className="app">
      <Nav />
      <main className="app__main">
        <Hero
          heroRenditionKey={heroRenditionKey}
          heroPhotoCredit={heroPhotoCredit}
        />
        <Gallery images={gallery} />
        <Investment pricing={pricing} />
        <About />
        <Faq entries={faqs} />
        <Booking />
      </main>
      <Footer />
    </div>
  );
}
